import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Eye, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { fmtMT } from "@/lib/mountainTime";
import { smsEventPreview, smsEventTest, goLive, goLiveSave, errText } from "@/lib/bidCenterApi";

const EVENTS = [["new_high_bid", "New valid high bid"], ["high_bid_correction", "High bid corrected"], ["bid_changed", "Edited or deleted bid"],
  ["needs_review", "Bid needs review"], ["closing_soon", "Auction closing soon"], ["completed", "Auction completed"]];
const Tick = ({ ok }) => (ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <XCircle className="h-4 w-4 text-red-600 shrink-0" />);

export function EventTest({ st, recipients, onSent }) {
  const [f, setF] = useState({ event_ref: "", event_type: "new_high_bid", horse_name: "", amount: "", note: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const req = useRef(null);
  const rc = recipients.find((r) => r.id === st.control.test_recipient_id);
  const body = () => ({ ...f, amount: f.amount === "" ? null : Number(f.amount), note: f.note || null });
  const set = (k) => (e) => { setF({ ...f, [k]: e.target.value }); setMsg(null); };
  const preview = async () => { try { setMsg((await smsEventPreview(body())).message); } catch (e) { toast.error(errText(e)); } };
  const send = async () => {
    if (!window.confirm(`Send ONE real test text for event ${f.event_ref} to ${rc.name} (…${rc.phone.slice(-4)}) now?`)) return;
    req.current = req.current || crypto.randomUUID();
    setBusy(true);
    try { const r = await smsEventTest({ ...body(), request_id: req.current, confirm: true }); toast.message(`Test: ${r.status}`); onSent(); }
    catch (e) { toast.error(errText(e)); } finally { req.current = null; setBusy(false); }
  };
  return (
    <div data-testid="sms-event-test" className="space-y-2 text-sm border-t pt-3">
      <p className="font-semibold">Event test text</p>
      <p className="text-xs text-stone-600">Type the reference and details of a Preview notification. Nothing is imported, and no auction, bid or alert is created.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        <Input data-testid="sms-event-ref" value={f.event_ref} onChange={set("event_ref")} placeholder="Event reference (e.g. notification ID)" className="h-11 sm:h-9" />
        <select data-testid="sms-event-type" value={f.event_type} onChange={set("event_type")} className="h-11 sm:h-9 rounded-md border px-2 bg-white text-sm">
          {EVENTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <Input data-testid="sms-event-horse" value={f.horse_name} onChange={set("horse_name")} placeholder="Horse name" className="h-11 sm:h-9" />
        <Input data-testid="sms-event-amount" type="number" min="0" value={f.amount} onChange={set("amount")} placeholder="Bid amount (optional)" className="h-11 sm:h-9" />
      </div>
      <Input data-testid="sms-event-note" value={f.note} onChange={set("note")} placeholder="Short note (optional)" className="h-11 sm:h-9" />
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" data-testid="sms-event-preview" disabled={!f.event_ref || !f.horse_name} onClick={preview} className="h-11 sm:h-9"><Eye className="h-4 w-4 mr-1" />Preview text (nothing sent)</Button>
        <Button data-testid="sms-event-send" disabled={busy || !msg || !rc || !st.can_send_tests} onClick={send} className="h-11 sm:h-9 bg-stone-800 text-white"><Send className="h-4 w-4 mr-1" />{busy ? "Sending…" : "Send event test"}</Button>
      </div>
      {msg && <pre data-testid="sms-event-message" className="whitespace-pre-wrap rounded-md bg-stone-50 border p-2 text-xs">{msg}</pre>}
    </div>
  );
}

function CheckItem({ it, onSave }) {
  const [ev, setEv] = useState(it.evidence || "");
  return (
    <li data-testid={`golive-item-${it.id}`} className="py-2 space-y-1">
      <div className="flex gap-1.5 items-start"><Tick ok={it.ok} /><span className="font-medium">{it.label}</span></div>
      {it.auto_evidence && <p className="text-xs text-stone-600 pl-6" data-testid={`golive-auto-${it.id}`}>System check: {it.auto_evidence}</p>}
      {it.verified ? (
        <div className="pl-6 text-xs text-stone-600 flex flex-wrap items-center gap-2">
          <span data-testid={`golive-signed-${it.id}`}>Verified by {it.by} · {fmtMT(it.at)} — {it.evidence}</span>
          <Button size="sm" variant="ghost" data-testid={`golive-undo-${it.id}`} onClick={() => onSave(it.id, { verified: false })} className="h-8">Undo</Button>
        </div>
      ) : (
        <div className="pl-6 flex flex-col sm:flex-row gap-2">
          <Textarea data-testid={`golive-evidence-${it.id}`} value={ev} onChange={(e) => setEv(e.target.value)} placeholder="What was tested and the result" className="min-h-[44px] text-xs" />
          <Button size="sm" data-testid={`golive-verify-${it.id}`} disabled={ev.trim().length < 10} onClick={() => onSave(it.id, { verified: true, evidence: ev })} className="h-10 sm:h-9 bg-stone-800 text-white">Mark verified</Button>
        </div>
      )}
    </li>
  );
}

export function GoLiveChecklist() {
  const [d, setD] = useState(null);
  const load = useCallback(() => { goLive().then(setD).catch((e) => toast.error(errText(e))); }, []);
  useEffect(() => { load(); }, [load]);
  if (!d) return null;
  const save = async (id, body) => { try { setD(await goLiveSave(id, body)); } catch (e) { toast.error(errText(e)); } };
  return (
    <div data-testid="golive-checklist" className="space-y-1 text-sm border-t pt-3">
      <div className="flex flex-wrap justify-between gap-2"><p className="font-semibold">Go-live checklist</p>
        <span data-testid="golive-status" className={`text-[11px] rounded-full px-2 py-0.5 font-semibold ${d.complete ? "bg-emerald-100 text-emerald-800" : "bg-[#FEF3C7] text-[#92400E]"}`}>{d.complete ? "Complete" : "Not complete — production automation stays off"}</span></div>
      <p className="text-xs text-stone-600">Required before automated auction SMS or automatic bid processing for existing auctions can be turned on in production. Sign-offs only count in the environment where they were made{d.environment === "preview" ? " (this is Preview)" : ""}.</p>
      <ul className="divide-y">{d.items.map((it) => <CheckItem key={`${it.id}-${it.verified}`} it={it} onSave={save} />)}</ul>
    </div>
  );
}
